/**
 * PermissionScreen
 *
 * Full-screen instruction page shown when the user has denied
 * mic/camera access. Covers iOS PWA, Android PWA, and web browser flows.
 *
 * Extracted from Roleplayv2.tsx lines 4532–4678.
 */

import { detectEnvironment } from "@/helper/helper";
import Image from "next/image";
import { useRouter } from "next/router";
import { useMemo } from "react";
import { BsHeadset } from "react-icons/bs";

interface PermissionScreenProps {
  isInterview: boolean;
}

export default function PermissionScreen({
  isInterview,
}: PermissionScreenProps) {
  const router = useRouter();

  const env = useMemo(() => {
    return detectEnvironment();
  }, []);

  return (
    <>
      {/* Issues Link */}
      <div
        className={`items-center h-8 mt-5 mb-2 mr-2 flex justify-end ${isInterview ? "font-gothic-regular" : ""}`}
      >
        <button
          onClick={() => router.push("/issues")}
          className=" text-mediumDark"
        >
          <div className="flex gap-1" title="Issues">
            <BsHeadset title="Issues" className="text-[#A1A5B7]" size={24} />
          </div>
        </button>
      </div>

      <main className="flex-1 px-4 overflow-x-hidden overflow-y-auto pt-6 pb-4">
        {env.isMobile && env.isPWA ? (
          env.isIOS ? (
            // IOS PWA
            <div className="flex flex-col w-full mx-auto max-w-xl space-y-10">
              <div className="bg-[#F1E5FF] px-4 pt-6 flex justify-center items-center rounded-2xl border border-white">
                <video
                  width={400}
                  src="/images/iosPermissionsPWA.mp4"
                  autoPlay
                  loop
                  muted
                  playsInline
                ></video>
              </div>
              <div className="flex flex-col gap-6 px-0 sm:px-2">
                <p className="text-left sm:text-xl text-lg font-semibold tracking-wide">
                  How to Enable Camera &amp; Mic for mple.ai?
                </p>

                <ol className="text-sm sm:text-base space-y-2 list-decimal px-4 tracking-wide">
                  <li>Open the Settings app.</li>
                  <li>
                    Use the search bar at the top and type &quot;mple ai&quot;.
                  </li>
                  <li>Tap on the mple.ai app in the results.</li>
                  <li>Turn ON the Camera and Microphone switches.</li>
                </ol>
              </div>
            </div>
          ) : (
            // Android PWA
            <div className="flex flex-col w-full mx-auto max-w-xl">
              <p className="text-left mb-3 sm:mx-1 mx-0 sm:text-xl text-lg font-semibold tracking-wide">
                Solution 1: App Info
              </p>
              <div className="bg-[#F1E5FF] px-4 pt-6 flex justify-center items-center rounded-2xl border border-white">
                <video
                  width={400}
                  src="/images/android_pwa2.mp4"
                  autoPlay
                  loop
                  muted
                  playsInline
                ></video>
              </div>
              <p className="text-left !my-6 sm:text-xl text-lg sm:mx-1 mx-0 font-semibold tracking-wide">
                Solution 2: Site Settings
              </p>
              <div className="bg-[#F1E5FF] px-4 pt-6 flex justify-center items-center rounded-2xl border border-white">
                <video
                  width={400}
                  src="/images/android_pwa1.mp4"
                  autoPlay
                  loop
                  muted
                  playsInline
                ></video>
              </div>
              <div className="flex !my-6 flex-col gap-6 px-0 sm:px-3">
                <p className="text-left sm:text-xl text-lg font-semibold tracking-wide">
                  How to Enable Camera &amp; Mic for mple.ai?
                </p>

                <ol className="text-sm sm:text-base space-y-2 list-decimal px-4 tracking-wide">
                  <li>Tap &amp; hold the mple.ai app icon.</li>
                  <li>Select App Info/Site Settings.</li>
                  <li>Go to Permissions.</li>
                  <li>
                    Tap Camera &amp; Microphone, then choose &quot;Allow only
                    while using the app&quot; (toggle switched ON).{" "}
                  </li>
                </ol>
              </div>
            </div>
          )
        ) : (
          // Web browser
          <div className=" w-full max-h-full px-4 py-6 md:py-12 rounded-lg flex gap-8 flex-col md:flex-row">
            <div className="flex-[2]">
              <Image
                src="/images/browser-permission.gif"
                alt="browser permission"
                width={400}
                height={400}
                className="w-full h-auto bg-slate-200 border rounded hidden md:block"
              />
              <Image
                src="/images/browser-permission-mobile.gif"
                alt="browser permission"
                width={400}
                height={400}
                className="w-full h-auto bg-slate-200 border rounded md:hidden"
              />
            </div>
            <div className="flex-[3]">
              <p className="text-lg font-bold mb-2">
                Mple is restricted from accessing your microphone and camera.
              </p>
              <ol className="list-decimal ml-4 text-[#4F4F4F] text-lg leading-8 mt-6">
                <li>
                  Click on page info icon in your browser&apos;s address bar
                </li>
                <li>Turn on camera and mic</li>
                <li>Refresh your page and try again</li>
              </ol>
              <p className="text-[#4F4F4F] text-lg mt-8">
                Note: Incase the issue still persists, consider changing your
                device and verify your camera and mic are not being used by some
                other application.
              </p>

              <button
                onClick={() => {
                  window.location.reload();
                }}
                className="my-4 bg-primary px-6 font-semibold py-2.5 rounded text-onPrimary hover:bg-primary/80 disabled:opacity-50"
              >
                Refresh
              </button>
            </div>
          </div>
        )}
      </main>
    </>
  );
}
